import { useEffect, useState } from "react";
import { Button } from "components/ui/button";
import { ArrowLeft, Plus, Globe, Trash2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Link } from "react-router-dom";

import AddCertification from "components/crewManagement/AddCertification";
import FeaturedWork from "./FeaturedWork";
import SocialLinksModal from "./SocialLinksModal";
import UploadResumePortfolio from "components/crewManagement/UploadResumePortfolio";
import { SOCIAL_ICONS } from "data/staticData";

import toast from "react-hot-toast";
import { signUpStep3 } from "services/authService";

export default function Step3Form({ data, setData, nextStep, prevStep }) {
  const [featuredWork, setFeaturedWork] = useState(data.featuredWork || []);
  const [links, setLinks] = useState(data.links || []);
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [resume, setResume] = useState(data.resume || null);
  const [portfolio, setPortfolio] = useState(data.portfolio || null);
  const [loading, setLoading] = useState(false);

  const showErrorToast = (title, message) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  const showSuccessToast = (title, message) => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      featuredWork,
      links,
      resume,
      portfolio,
    }));
  }, [featuredWork, links, resume, portfolio, setData]);

  const handleSubmit = async () => {
    if (!data.crew_member_id) {
      showErrorToast("Invalid Signup Flow", "Please restart the signup process.");
      return;
    }

    if (!featuredWork || featuredWork.length === 0) {
      showErrorToast("Validation Error", "Please upload at least one piece of Featured Work.");
      return;
    }

    try {
    setLoading(true);
    const formData = new FormData();
    formData.append("crew_member_id", String(data.crew_member_id));

    // 1. Handle Resume (Single)
    const resumeFile = resume instanceof File ? resume : resume?.file;
    if (resumeFile instanceof File) {
      formData.append("resume", resumeFile);
    }

    // 2. Handle Portfolio (Multiple) - UPDATED SECTION
    if (Array.isArray(portfolio)) {
      portfolio.forEach((item) => {
        const file = item instanceof File ? item : item?.file;
        if (file instanceof File) {
          formData.append("portfolio", file); // Backend usually expects multiple entries for the same key
        }
      });
    }

    // 3. Handle Certifications
    (data.certifications || []).forEach((item) => {
      const file = item instanceof File ? item : item?.file;
      if (file instanceof File) {
        formData.append("certifications", file);
      }
    });

    // 4. Handle Featured Work
    featuredWork.forEach((item) => {
      const title = item.title || "Default Title";  // Default title
      const tag = item.tags || ["default_tag"];    // Default tags

      if (item.files && Array.isArray(item.files)) {
        item.files.forEach((file) => {
          if (file instanceof File) {
            formData.append("recent_work", file);
            formData.append("title", title); // Send title for each file
            formData.append("tag", JSON.stringify(tag)); // Send tags as JSON
          }
        });
      }
    });

      const socialLinksPayload = {};
      links.forEach((l) => {
        if (l.platform && l.url) {
          socialLinksPayload[l.platform] = l.url;
        }
      });

      formData.append(
        "social_media_links",
        JSON.stringify(socialLinksPayload)
      );

      await signUpStep3(formData);

      showSuccessToast("Profile Created", "Your profile has been created successfully.");
      nextStep();
    } catch (err) {
      console.error("Step 3 error:", err);
      showErrorToast(
        "Step 3 Failed",
        err?.response?.data?.message || err?.message || "Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteLink = (id) => {
    setLinks((prev) => prev.filter((l) => l.id !== id));
  };

  return (
    <div className="flex flex-col gap-10">
      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        <div className="flex flex-col gap-6">

          {/* Social Links (Optional) */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium">Social Media Links (Optional)</label>
            {links.map((link) => (
              <SocMedLink
                key={link.id}
                socmedItem={link}
                deleteLink={deleteLink}
              />
            ))}

            <button
              type="button"
              className="flex gap-2 items-center"
              onClick={() => setSocialModalOpen(true)}
            >
              <div className="p-2 rounded-full border border-black/10 hover:bg-black/5">
                <Plus className="w-4 h-4 text-black" />
              </div>
              <span className="text-sm">Add {links.length > 0 ? "another" : ""} link</span>
            </button>
          </div>

          {/* Featured Work (REQUIRED) */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold">
              Featured Work / Recent Work <span className="text-red-500">*</span>
            </label>
            <FeaturedWork value={featuredWork} onChange={setFeaturedWork} />
          </div>

          {/* Certifications (Optional) */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold">Certifications (Optional)</label>
            <AddCertification
              value={data.certifications || []}
              onChange={(v) =>
                setData((prev) => ({ ...prev, certifications: v }))
              }
              bg="bg-white"
            />
          </div>

          {/* Resume & Portfolio (Optional) */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold">Documents (Optional)</label>
            <UploadResumePortfolio
              resume={resume}
              setResume={setResume}
              portfolio={portfolio}
              setPortfolio={setPortfolio}
              bgColour="bg-white"
              buttonBgColour="bg-white hover:bg-black/10"
            />
          </div>
        </div>
      </form>

      <div className="flex justify-start gap-4">
        <button
          type="button"
          onClick={prevStep}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-black/10 hover:bg-black/5"
        >
          <ArrowLeft className="w-5 h-5 text-black" />
        </button>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="flex items-center justify-center rounded-full border border-black/10 hover:bg-black/70 px-4 py-2"
        >
          {loading ? "Submitting..." : "Create Profile"}
        </Button>
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-600 mb-10">
        <hr className="flex-grow" />
        <span>Already have an account?</span>
        <Link to="/loginCrew" className="text-black">
          Log in
        </Link>
        <hr className="flex-grow" />
      </div>

      <SocialLinksModal
        open={socialModalOpen}
        onClose={() => setSocialModalOpen(false)}
        links={links}
        onChange={setLinks}
      />
    </div>
  );
}

const SocMedLink = ({ socmedItem, deleteLink }) => {
  const platform = SOCIAL_ICONS.find((p) => p.id === socmedItem.platform);

  return (
    <div className="w-full flex items-center justify-between bg-white border border-[#EADBC8] px-3 py-1 rounded-lg">
      <div className="flex items-center gap-3">
        {platform?.src ? (
          <img src={platform.src} alt="" className="w-6 h-6" />
        ) : platform?.icon ? (
          <platform.icon className="w-6 h-6 text-[#9C8662]" />
        ) : (
          <Globe className="w-6 h-6 text-[#9C8662]" />
        )}

        <span className="text-gray-700 text-base font-medium">
          {socmedItem.name}
        </span>
      </div>

      <button
        type="button"
        onClick={() => deleteLink(socmedItem.id)}
        className="p-2 rounded-lg hover:bg-black/5"
      >
        <Trash2 size={18} className="text-gray-500" />
      </button>
    </div>
  );
};
